// カテゴリグループ新規作成・編集用共通Store
export interface TableRow {
    categoryId: string;
    label: string;
    slug: string;
    labelError: string;
    slugError: string;
    count?: number;
}

import {action, makeObservable, observable, runInAction} from "mobx";
import {v4 as uuidv4} from "uuid";

import {validateLength} from "../utils/validation/functions/length";
import {validateSlug} from "../utils/validation/functions/slug";
import {ValidationResultItem} from "../utils/validation/validation-result-item";

export abstract class CategoryGroupFormStore {
    @observable public groupLabel = "";
    @observable public groupSlug = "";
    @observable public groupLabelError = "";
    @observable public groupSlugError = "";
    @observable public duplicateError = "";
    @observable public tableRows: TableRow[] = [];

    constructor() {
        makeObservable(this);
        runInAction(() => {
            this.tableRows = [this.createEmptyRow()];
        });
    }

    protected createEmptyRow(): TableRow {
        return {
            categoryId: uuidv4(),
            label: "",
            slug: "",
            labelError: "",
            slugError: "",
            count: 0,
        };
    }

    @action.bound
    public async setGroupLabel(newLabel: string) {
        this.groupLabel = newLabel;
        // 必須チェック
        if (!newLabel.trim()) {
            this.groupLabelError = "カテゴリグループ名は必須です。";
            return;
        }
        // 長さチェック
        const errs: ValidationResultItem[] = await Promise.resolve(
            validateLength(1, 50)(newLabel.trim()),
        );
        runInAction(() => {
            // エラーメッセージをセット
            this.groupLabelError = errs.length ? errs[0].errorMessage : "";
        });
    }

    @action.bound
    public async setGroupSlug(newSlug: string) {
        this.groupSlug = newSlug;
        if (!newSlug.trim()) {
            this.groupSlugError = "識別子(slug)は必須です。";
            return;
        }
        const lenErrs: ValidationResultItem[] = await Promise.resolve(
            validateLength(1, 50)(newSlug.trim()),
        );
        if (lenErrs.length) {
            runInAction(() => {
                // スラッグの長さチェック
                this.groupSlugError = lenErrs[0].errorMessage;
            });
            return;
        }
        const slugErrs: ValidationResultItem[] = await Promise.resolve(
            validateSlug()(newSlug.trim()),
        );
        runInAction(() => {
            // スラッグの形式チェック
            this.groupSlugError = slugErrs.length ? slugErrs[0].errorMessage : "";
        });
    }

    @action.bound
    public addCategory() {
        this.tableRows.push(this.createEmptyRow());
    }

    @action.bound
    public removeCategory(id: string) {
        this.tableRows = this.tableRows.filter((r) => r.categoryId !== id);
    }

    @action.bound
    public async setLabel(id: string, newLabel: string) {
        const row = this.tableRows.find((r) => r.categoryId === id);
        if (!row) return;
        row.label = newLabel;
        if (!newLabel.trim()) {
            row.labelError = "カテゴリ名は必須です。";
            return;
        }
        const errs: ValidationResultItem[] = await Promise.resolve(
            validateLength(1, 50)(newLabel.trim()),
        );
        runInAction(() => {
            // 長さチェック
            row.labelError = errs.length ? errs[0].errorMessage : "";
        });
    }

    @action.bound
    public async setSlug(id: string, newSlug: string) {
        const row = this.tableRows.find((r) => r.categoryId === id);
        if (!row) return;
        row.slug = newSlug;

        if (!newSlug.trim()) {
            row.slugError = "識別子(slug)は必須です。";
            return;
        }

        const lenErrs = await Promise.resolve(validateLength(1, 50)(newSlug.trim()));
        if (lenErrs.length) {
            runInAction(() => {
                row.slugError = lenErrs[0].errorMessage;
            });

            return;
        }

        const slugErrs = await Promise.resolve(validateSlug()(newSlug.trim()));
        if (slugErrs.length) {
            runInAction(() => {
                // スラッグの形式チェック
                row.slugError = slugErrs[0].errorMessage;
            });
            return;
        }

        // 重複チェックを全体で再評価
        runInAction(() => {
            const slugCounts = new Map<string, number>();

            // slug の出現数を集計
            for (const r of this.tableRows) {
                const trimmed = r.slug.trim();
                if (!trimmed) continue;
                slugCounts.set(trimmed, (slugCounts.get(trimmed) ?? 0) + 1);
            }

            // 各行にエラーメッセージを再設定
            for (const r of this.tableRows) {
                const trimmed = r.slug.trim();
                if (!trimmed) continue;
                r.slugError =
                    (slugCounts.get(trimmed) ?? 0) > 1
                        ? "カテゴリ識別子(slug)が重複しています。"
                        : "";
            }
        });
    }

    public canSave(): boolean {
        if (this.groupLabelError || this.groupSlugError) return false;
        if (!this.groupLabel.trim() || !this.groupSlug.trim()) return false;
        for (const r of this.tableRows) {
            if (r.labelError || r.slugError) return false;
            if (!r.label.trim() || !r.slug.trim()) return false;
        }
        return true;
    }
}
